import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "../services/api";
import { subscribe, unsubscribe } from "../utils/events";
import {
  assignedOnlyPrefEnabled,
  categoryForEvent,
  channelAllowsInApp,
  readCachedPrefs,
  writeCachedPrefs,
  PREFS_UPDATED_EVENT,
  passesAssignedOnlyFilter,
} from "../utils/dmeNotificationPrefs";

const TOAST_KEY = {
  ivr_reviewed: { completed: "ivrCompleted", denied: "ivrDenied" },
  form1500_reviewed: { cleared: "form1500Cleared", incomplete: "form1500Incomplete" },
  eob_received: { received: "eobReceived" },
};

const TOAST_FALLBACK = {
  ivrCompleted: "IVR cleared for order {{orderId}}",
  ivrDenied: "IVR denied for order {{orderId}}",
  form1500Cleared: "1500 form cleared for order {{orderId}}",
  form1500Incomplete: "1500 form needs corrections for order {{orderId}}",
  eobReceived: "EOB received for order {{orderId}}",
};

// In-app half of the DME review notifications (spec 2026-07-30). The email
// half is server-side (Task 8/backend); this only decides whether to toast
// the viewer when an `orderUpdated` order-update SSE arrives. Pub/sub here
// is the plain document.addEventListener wrapper in src/utils/events.js —
// there is no usePubSub hook in this codebase (confirmed against
// src/pages/Orders.js, which subscribes to "orderUpdated" the same way).
export default function useDmeOrderToasts({ isProviderOrg, currentUserId, onOpenOrder, userCustom, isDoctor = false }) {
  const { t } = useTranslation();
  const prefsRef = useRef(readCachedPrefs());
  // Derived here, not passed as a boolean, so the product default (absent
  // pref = assigned-only ON) is encoded exactly once — a caller that omits
  // userCustom gets the real default, not its inverse.
  const assignedOnly = assignedOnlyPrefEnabled(userCustom);

  // Non-provider orgs never fetch prefs — there's no in-app notification
  // surface for them and the settings screen is provider-only.
  useEffect(() => {
    if (!isProviderOrg) return undefined;
    let cancelled = false;

    const load = async () => {
      try {
        const rows = await api.getDmeNotificationPrefs();
        if (cancelled) return;
        const map = {};
        for (const row of rows || []) {
          map[row.category] = row.channel;
        }
        prefsRef.current = map;
        writeCachedPrefs(map);
      } catch {
        // Fail open — an absent pref defaults to "both" via
        // channelAllowsInApp, so a failed fetch never silently swallows
        // notifications the user is entitled to see.
      }
    };
    load();

    const onPrefsUpdated = (e) => {
      prefsRef.current = e?.detail || {};
    };
    window.addEventListener(PREFS_UPDATED_EVENT, onPrefsUpdated);
    return () => {
      cancelled = true;
      window.removeEventListener(PREFS_UPDATED_EVENT, onPrefsUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProviderOrg]);

  useEffect(() => {
    if (!isProviderOrg) return undefined;

    const handler = (evt) => {
      const detail = evt?.detail || {};

      const category = categoryForEvent(detail.event, detail.outcome);
      if (!category) return;
      if (detail.audience !== "provider") return;
      // String-coerced like every other id comparison in the orders code —
      // the backend emits actorUserId through a string template while
      // auth.user.id can be numeric.
      if (detail.actorUserId && currentUserId != null && String(detail.actorUserId) === String(currentUserId)) return;
      if (!channelAllowsInApp(prefsRef.current[category])) return;
      if (
        !passesAssignedOnlyFilter({
          enabled: assignedOnly,
          isDoctor,
          currentUserId,
          assignedToKey: detail?.data?.assignedTo?.key,
        })
      )
        return;

      const key = (TOAST_KEY[detail.event] || {})[detail.outcome];
      if (!key) return;

      const orderId = detail?.data?.id || detail.orderId || "";
      toast.info(t(`orders.notifSettings.toast.${key}`, TOAST_FALLBACK[key], { orderId }), {
        onClick: () => onOpenOrder && onOpenOrder(orderId, detail.event === "eob_received" ? "attachments" : null),
      });
    };

    subscribe("orderUpdated", handler);
    return () => unsubscribe("orderUpdated", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProviderOrg, currentUserId, onOpenOrder, assignedOnly, isDoctor]);
}
